import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Beaker, Upload, Sparkles, FileText, Zap, Search, Atom, Clock, ArrowRight, Check, ChevronRight, Activity, FlaskConical, Database, Linkedin, Twitter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import SEO from '@/components/SEO';
import { 
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine 
} from 'recharts';

// Sample FTIR spectrum data for demonstration
const DEMO_FTIR_DATA = [
  { x: 4000, y: 98.5 }, { x: 3800, y: 97.2 }, { x: 3600, y: 95.8 },
  { x: 3500, y: 82.4 }, { x: 3450, y: 65.2 }, { x: 3400, y: 45.8 },
  { x: 3350, y: 58.9 }, { x: 3300, y: 72.4 }, { x: 3200, y: 85.6 },
  { x: 3000, y: 78.9 }, { x: 2950, y: 52.3 }, { x: 2920, y: 35.6 },
  { x: 2850, y: 42.8 }, { x: 2800, y: 68.5 }, { x: 2600, y: 88.2 },
  { x: 2400, y: 92.5 }, { x: 2200, y: 94.8 }, { x: 2000, y: 96.2 },
  { x: 1800, y: 89.5 }, { x: 1750, y: 45.2 }, { x: 1715, y: 22.8 },
  { x: 1680, y: 38.5 }, { x: 1650, y: 52.4 }, { x: 1600, y: 68.9 },
  { x: 1550, y: 75.2 }, { x: 1500, y: 82.4 }, { x: 1450, y: 68.5 },
  { x: 1400, y: 72.8 }, { x: 1350, y: 78.9 }, { x: 1300, y: 82.5 },
  { x: 1250, y: 75.8 }, { x: 1200, y: 62.4 }, { x: 1150, y: 55.8 },
  { x: 1100, y: 48.9 }, { x: 1050, y: 42.5 }, { x: 1000, y: 52.8 },
  { x: 950, y: 65.4 }, { x: 900, y: 72.8 }, { x: 850, y: 78.5 },
  { x: 800, y: 82.4 }, { x: 750, y: 75.8 }, { x: 700, y: 68.9 },
  { x: 650, y: 72.5 }, { x: 600, y: 78.2 }
];

// Detected peaks with annotations
const DEMO_PEAKS = [
  { x: 1715, y: 22.8, label: "1715 cm⁻¹", group: "C=O Carbonyl", color: "#ef4444" },
  { x: 2920, y: 35.6, label: "2920 cm⁻¹", group: "C-H Alkyl", color: "#f59e0b" },
  { x: 3400, y: 45.8, label: "3400 cm⁻¹", group: "O-H Hydroxyl", color: "#10b981" },
  { x: 1050, y: 42.5, label: "1050 cm⁻¹", group: "C-O Ether", color: "#8b5cf6" },
];

// Functional groups detected
const DEMO_FUNCTIONAL_GROUPS = [
  { name: "Carbonyl C=O", confidence: 98, color: "from-red-500 to-red-600" },
  { name: "Hydroxyl O-H", confidence: 95, color: "from-emerald-500 to-emerald-600" },
  { name: "Alkyl C-H", confidence: 92, color: "from-amber-500 to-amber-600" },
  { name: "Ether C-O", confidence: 88, color: "from-violet-500 to-violet-600" },
];

// Possible compounds
const DEMO_COMPOUNDS = [
  { name: "Ethyl Acetate", match: 94 },
  { name: "Acetic Acid", match: 87 },
  { name: "Butyl Alcohol", match: 82 },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      <SEO 
        title="AI Spectral & Chromatographic Analysis"
        description="Upload your FTIR or HPLC data and get instant AI-powered insights. Automatic peak detection, functional group identification, and compound matching."
        keywords="spectroscopy, chromatography, FTIR analysis, HPLC analysis, AI laboratory, peak detection, functional groups, spectral analysis"
        url="/"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/tabedlab-logo.png" 
              alt="TabedLab Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              TabedLab
            </span>
          </div>
          
          {/* Navigation Links */}
          <nav className="hidden md:flex items-center gap-6">
            <button
              onClick={() => navigate('/demo')}
              className="text-slate-400 hover:text-white transition-colors"
            >
              Demo
            </button>
            <button
              onClick={() => navigate('/blog')}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="header-blog-link"
            >
              Blog
            </button>
          </nav>
          
          {user ? (
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              data-testid="dashboard-button"
            >
              Go to Dashboard
            </Button>
          ) : (
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-slate-300 hover:text-white hover:bg-slate-800"
                data-testid="login-button-header"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
                data-testid="register-button-header"
              >
                Sign Up Free
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Section 1: Hero - Simple & Clear */}
      <section className="relative pt-32 pb-16 px-6 min-h-[80vh] flex items-center">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          <div className="absolute top-40 right-1/4 w-80 h-80 bg-blue-600/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-5xl mx-auto text-center w-full">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              AI Spectral & Chromatographic
            </span>
            <br />
            <span className="text-white">Analysis</span>
          </h1>

          <p className="text-xl sm:text-2xl text-slate-400 max-w-3xl mx-auto mb-12">
            Upload your data. Get instant AI-powered insights.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg px-10 py-7 rounded-xl shadow-lg shadow-cyan-500/25"
              data-testid="get-started-button"
            >
              <Zap className="mr-2 w-5 h-5" />
              Start Free Analysis
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/demo')}
              className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white text-lg px-10 py-7 rounded-xl"
              data-testid="watch-demo-button"
            >
              Try Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>

          {/* Supported formats badge */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
            <span className="text-slate-500 text-sm">Supported:</span>
            {['FTIR', 'HPLC'].map((type) => (
              <span key={type} className="px-3 py-1 text-sm bg-slate-800/50 border border-slate-700 rounded-full text-slate-400">
                {type}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: Live Demo - Visual Demonstration */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
              <Activity className="w-4 h-4 text-cyan-400" />
              <span className="text-sm text-cyan-300">Live Demonstration</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              <span className="text-white">See AI Analysis </span>
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">in Action</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Real FTIR spectrum with AI-detected peaks and functional groups
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-6">
            {/* Chart Section - Takes 3 columns */}
            <div className="lg:col-span-3 bg-slate-800/50 border border-slate-700 rounded-2xl p-5 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-cyan-400" />
                  FTIR Spectrum Analysis
                </h3>
                <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400 border border-green-500/30">
                  Live Demo
                </span>
              </div>
              
              <div className="bg-slate-900/70 rounded-xl p-3 border border-slate-700">
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={DEMO_FTIR_DATA} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                    <defs>
                      <linearGradient id="demoGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis 
                      dataKey="x" 
                      stroke="#64748b"
                      tick={{ fill: '#94a3b8', fontSize: 10 }}
                      label={{ value: 'Wavenumber (cm⁻¹)', position: 'insideBottom', offset: -10, fill: '#94a3b8', fontSize: 11 }}
                      reversed
                    />
                    <YAxis 
                      stroke="#64748b"
                      tick={{ fill: '#94a3b8', fontSize: 10 }}
                      label={{ value: 'Transmittance (%)', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }}
                      domain={[0, 100]}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(30, 41, 59, 0.95)',
                        border: '1px solid #475569',
                        borderRadius: '8px',
                        color: '#e2e8f0'
                      }}
                      formatter={(value) => [`${value.toFixed(1)}%`, 'Transmittance']}
                      labelFormatter={(label) => `${label} cm⁻¹`}
                    />
                    <Area
                      type="monotone"
                      dataKey="y"
                      stroke="none"
                      fill="url(#demoGradient)"
                    />
                    <Line
                      type="monotone"
                      dataKey="y"
                      stroke="#06b6d4"
                      strokeWidth={2}
                      dot={false}
                    />
                    {/* Peak markers */}
                    {DEMO_PEAKS.map((peak, index) => (
                      <ReferenceLine
                        key={index}
                        x={peak.x}
                        stroke={peak.color}
                        strokeDasharray="4 4"
                        strokeWidth={2}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
                
                {/* Peak Labels */}
                <div className="flex flex-wrap gap-2 mt-3 justify-center">
                  {DEMO_PEAKS.map((peak, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs"
                      style={{ backgroundColor: `${peak.color}20`, border: `1px solid ${peak.color}40` }}
                    >
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: peak.color }}></div>
                      <span className="text-white font-medium">{peak.label}</span>
                      <span className="text-slate-400">→</span>
                      <span className="text-slate-300">{peak.group}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Analysis Results - Takes 2 columns */}
            <div className="lg:col-span-2 space-y-4">
              {/* Functional Groups */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5 backdrop-blur-sm">
                <h3 className="text-base font-semibold text-white flex items-center gap-2 mb-3">
                  <FlaskConical className="w-4 h-4 text-purple-400" />
                  Functional Groups Detected
                </h3>
                <div className="space-y-2">
                  {DEMO_FUNCTIONAL_GROUPS.map((group, index) => (
                    <div key={index} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-300">{group.name}</span>
                        <span className="text-xs font-semibold text-white">{group.confidence}%</span>
                      </div>
                      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full bg-gradient-to-r ${group.color} rounded-full`}
                          style={{ width: `${group.confidence}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Possible Compounds */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-5 backdrop-blur-sm">
                <h3 className="text-base font-semibold text-white flex items-center gap-2 mb-3">
                  <Database className="w-4 h-4 text-amber-400" />
                  Database Match
                </h3>
                <div className="space-y-2">
                  {DEMO_COMPOUNDS.map((compound, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded flex items-center justify-center text-white font-bold text-xs ${
                          index === 0 ? 'bg-gradient-to-br from-amber-500 to-amber-600' :
                          index === 1 ? 'bg-gradient-to-br from-slate-500 to-slate-600' :
                          'bg-gradient-to-br from-orange-700 to-orange-800'
                        }`}>
                          {index + 1}
                        </div>
                        <span className="text-white text-sm font-medium">{compound.name}</span>
                      </div>
                      <span className={`text-xs font-semibold ${
                        compound.match >= 90 ? 'text-green-400' :
                        compound.match >= 80 ? 'text-amber-400' : 'text-slate-400'
                      }`}>
                        {compound.match}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <Button
                onClick={() => navigate('/demo')}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-5 text-base rounded-xl"
                data-testid="try-demo-landing"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Try It Yourself - Free Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: How It Works */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              <span className="text-white">How It </span>
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Works</span>
            </h2>
            <p className="text-slate-400 text-lg">Three simple steps to professional analysis</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection Lines (Desktop) */}
            <div className="hidden md:block absolute top-16 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500"></div>
            
            {/* Step 1: Submit */}
            <div className="relative group">
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10">
                <div className="relative inline-flex">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center mb-6 mx-auto shadow-lg shadow-cyan-500/30">
                    <Upload className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-slate-900 border-2 border-cyan-500 flex items-center justify-center text-cyan-400 font-bold text-sm">
                    1
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Submit</h3>
                <p className="text-slate-400">
                  Upload your spectral data file (CSV, Excel, PDF, or image)
                </p>
              </div>
              <ChevronRight className="hidden md:block absolute top-16 -right-4 w-8 h-8 text-slate-600 z-10" />
            </div>

            {/* Step 2: Analysis */}
            <div className="relative group">
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center hover:border-blue-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
                <div className="relative inline-flex">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6 mx-auto shadow-lg shadow-blue-500/30">
                    <Atom className="w-10 h-10 text-white animate-pulse" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-slate-900 border-2 border-blue-500 flex items-center justify-center text-blue-400 font-bold text-sm">
                    2
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Analysis</h3>
                <p className="text-slate-400">
                  AI processes your data with advanced spectral algorithms
                </p>
              </div>
              <ChevronRight className="hidden md:block absolute top-16 -right-4 w-8 h-8 text-slate-600 z-10" />
            </div>

            {/* Step 3: Results */}
            <div className="relative group">
              <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                <div className="relative inline-flex">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-6 mx-auto shadow-lg shadow-purple-500/30">
                    <FileText className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-slate-900 border-2 border-purple-500 flex items-center justify-center text-purple-400 font-bold text-sm">
                    3
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Results</h3>
                <p className="text-slate-400">
                  Get detailed reports with interpretations and recommendations
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Benefits */}
      <section className="py-24 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              <span className="text-white">Why Choose </span>
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">TabedLab?</span>
            </h2>
            <p className="text-slate-400 text-lg">Powerful features designed for laboratory professionals</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Benefit 1 */}
            <div className="group">
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border border-slate-700 rounded-2xl p-8 h-full hover:border-cyan-500/30 transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Search className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Automatic Peak Detection</h3>
                <p className="text-slate-400">
                  Advanced algorithms automatically identify and label peaks in your spectral data with high precision.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-cyan-400" />
                    Multi-peak recognition
                  </li>
                  <li className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-cyan-400" />
                    Noise filtering
                  </li>
                </ul>
              </div>
            </div>

            {/* Benefit 2 */}
            <div className="group">
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border border-slate-700 rounded-2xl p-8 h-full hover:border-blue-500/30 transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Atom className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Functional Group Identification</h3>
                <p className="text-slate-400">
                  AI-powered identification of functional groups based on spectral patterns and reference databases.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-blue-400" />
                    FTIR interpretation
                  </li>
                  <li className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-blue-400" />
                    Chemical structure hints
                  </li>
                </ul>
              </div>
            </div>

            {/* Benefit 3 */}
            <div className="group">
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border border-slate-700 rounded-2xl p-8 h-full hover:border-purple-500/30 transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Clock className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Analysis in Seconds</h3>
                <p className="text-slate-400">
                  Get comprehensive results in seconds, not hours. Speed up your workflow dramatically.
                </p>
                <ul className="mt-4 space-y-2">
                  <li className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-purple-400" />
                    Instant processing
                  </li>
                  <li className="flex items-center gap-2 text-slate-300 text-sm">
                    <Check className="w-4 h-4 text-purple-400" />
                    PDF report download
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Call to Action */}
      <section className="py-24 px-6 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 via-blue-600/20 to-purple-600/20"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
            <Zap className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-300">Start with 5 free analyses</span>
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            <span className="text-white">Try </span>
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">TabedLab AI</span>
            <span className="text-white"> for Free</span>
          </h2>

          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Join thousands of chemists and researchers who trust TabedLab for their spectral analysis needs.
          </p>

          <Button
            onClick={handleGetStarted}
            size="lg"
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-xl px-12 py-7 rounded-xl shadow-2xl shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all"
            data-testid="cta-button"
          >
            <Sparkles className="mr-2 w-6 h-6" />
            Get Started - It's Free
          </Button>

          <p className="mt-6 text-slate-500 text-sm">
            No credit card required • 5 free analyses included
          </p>
        </div>
      </section>

      {/* Section 5: Register to Get Results */}
      <section className="py-24 px-6 bg-slate-900">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700 rounded-3xl p-12 text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>
            
            <div className="relative">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 mb-8 shadow-lg shadow-cyan-500/30">
                <Beaker className="w-10 h-10 text-white" />
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                <span className="text-white">Register to Get </span>
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Results</span>
              </h2>

              <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
                Create your free account in seconds and start analyzing your spectral data with the power of AI.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  onClick={() => navigate('/register')}
                  size="lg"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg px-10 py-6 rounded-xl"
                  data-testid="register-cta-button"
                >
                  Create Free Account
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white text-lg px-10 py-6 rounded-xl"
                >
                  Already have an account?
                </Button>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-8 max-w-lg mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">5</div>
                  <div className="text-sm text-slate-500">Free Analyses</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-400">5</div>
                  <div className="text-sm text-slate-500">Analysis Types</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-400">4</div>
                  <div className="text-sm text-slate-500">Languages</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-5 gap-8 mb-8">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/tabedlab-logo.png" 
                  alt="TabedLab Logo" 
                  className="w-10 h-10 object-contain"
                />
                <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  TabedLab
                </span>
              </div>
              <p className="text-slate-400 text-sm max-w-sm mb-4">
                AI-powered spectral analysis platform for chemists and laboratory professionals.
              </p>
              {/* Social Media Links */}
              <div className="flex items-center gap-3">
                <a
                  href="https://www.linkedin.com/company/tabedlab"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all"
                  data-testid="linkedin-link"
                  aria-label="Follow TabedLab on LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://x.com/tabedlab"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/50 transition-all"
                  data-testid="twitter-link"
                  aria-label="Follow TabedLab on X (Twitter)"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <button
                    onClick={() => navigate('/demo')}
                    className="text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    Try Demo
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/register')}
                    className="text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    Sign Up Free
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <button
                    onClick={() => navigate('/about')}
                    className="text-slate-400 hover:text-cyan-400 transition-colors"
                    data-testid="footer-about-link"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/blog')}
                    className="text-slate-400 hover:text-cyan-400 transition-colors"
                    data-testid="footer-blog-link"
                  >
                    Blog
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Support</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <button
                    onClick={() => navigate('/faq')}
                    className="text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    FAQ
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/contact')}
                    className="text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    Contact Us
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-3 text-sm">
                <li>
                  <button
                    onClick={() => navigate('/privacy-policy')}
                    className="text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    Privacy Policy
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-500 text-sm">
            <p>&copy; {new Date().getFullYear()} TabedLab. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
